/**
 * @voltxampere/web — `<vxa-formula-overlay>` holographic panel (S56b)
 *
 * Canvas-level overlay that surfaces the engine-side formula
 * detector match. Renders a floating panel with the template name,
 * symbolic LaTeX, substituted numeric LaTeX, and the running
 * numerical result.
 *
 * Topology-aware: Inspector formula (Sprint 14) renders
 * single-component laws; this overlay renders multi-component
 * pattern recognition (voltage divider, RC filter, op-amp gain,
 * BJT CE, LED limit). Both can be visible simultaneously without
 * conflict — different abstraction levels.
 *
 * Lit reactive: subscribes to `currentFormulaMatch` (debounced
 * detector output) + `formulaOverlayOpen` (user-controlled
 * visibility toggle). KaTeX renders on every match change.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { renderRawLatex } from "@voltxampere/ui";
import type { MatchResult } from "@voltxampere/engine";

import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import {
  currentFormulaMatch,
  formulaDetectionDurationMs,
  formulaMatchCount,
  formulaOverlayOpen,
  toggleFormulaOverlay,
} from "../store/formula-overlay.js";

const TEMPLATE_NAME_KEY: Readonly<Record<MatchResult["templateId"], TranslationKey>> = {
  "voltage-divider": "formula.voltage-divider.name",
  "rc-filter": "formula.rc-filter.name",
  "led-current-limit": "formula.led-current-limit.name",
  "opamp-inverting": "formula.opamp-inverting.name",
  "opamp-non-inverting": "formula.opamp-non-inverting.name",
  "bjt-common-emitter": "formula.bjt-common-emitter.name",
};

@customElement("vxa-formula-overlay")
export class VxaFormulaOverlay extends LitElement {
  private readonly _match = new SignalController(this, currentFormulaMatch);
  private readonly _open = new SignalController(this, formulaOverlayOpen);
  private readonly _duration = new SignalController(this, formulaDetectionDurationMs);
  private readonly _count = new SignalController(this, formulaMatchCount);

  static override styles = css`
    :host {
      position: absolute;
      bottom: var(--s-5);
      right: var(--s-5);
      z-index: var(--z-overlay, 10);
      pointer-events: none;
    }

    :host([data-hidden]) {
      display: none;
    }

    .panel {
      pointer-events: auto;
      width: min(380px, 40vw);
      background: rgba(8, 12, 22, 0.92);
      border: 1px solid var(--brass-faint);
      box-shadow:
        0 0 0 1px var(--brass-ghost),
        0 8px 24px rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      display: flex;
      flex-direction: column;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--s-3) var(--s-4);
      border-bottom: 1px solid var(--ivory-wisp);
    }

    .titles {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .eyebrow {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--brass);
    }

    .name {
      font-family: var(--font-display);
      font-size: var(--fs-body);
      color: var(--ivory);
    }

    .close {
      background: transparent;
      border: 1px solid var(--ivory-wisp);
      color: var(--ivory-dim);
      padding: 2px 6px;
      cursor: pointer;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
    }

    .close:hover {
      color: var(--ivory);
      border-color: var(--ivory);
    }

    .body {
      padding: var(--s-3) var(--s-4);
      display: flex;
      flex-direction: column;
      gap: var(--s-3);
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .field-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--ivory-faint);
    }

    .formula-host {
      padding: var(--s-2) 0;
      color: var(--ivory);
      font-size: var(--fs-body);
    }

    .result {
      display: flex;
      align-items: baseline;
      gap: var(--s-2);
      padding-top: var(--s-2);
      border-top: 1px dashed var(--ivory-wisp);
    }

    .result-value {
      font-family: var(--font-display);
      font-size: var(--fs-h3);
      color: var(--brass-hot, #d4a857);
    }

    .result-unit {
      font-family: var(--font-mono);
      font-size: var(--fs-small);
      color: var(--ivory-dim);
      letter-spacing: 0.12em;
    }

    .footer {
      padding: var(--s-2) var(--s-4);
      border-top: 1px solid var(--ivory-wisp);
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.18em;
      color: var(--ivory-faint);
      text-transform: uppercase;
      display: flex;
      gap: var(--s-3);
    }
  `;

  override updated(): void {
    const match = this._match.value;
    if (!match) return;
    const symbolicHost = this.shadowRoot?.querySelector<HTMLElement>("[data-host=symbolic]");
    const numericHost = this.shadowRoot?.querySelector<HTMLElement>("[data-host=numeric]");
    if (symbolicHost) renderRawLatex(match.symbolic, symbolicHost);
    if (numericHost) renderRawLatex(match.numeric.formula, numericHost);
  }

  override render() {
    const match = this._match.value;
    const open = this._open.value;
    const hidden = !open || match === null;
    this.toggleAttribute("data-hidden", hidden);
    if (hidden) return nothing;
    return html`
      <div class="panel" role="region" aria-label=${t("formula.overlay.title")}>
        <div class="header">
          <div class="titles">
            <span class="eyebrow">${t("formula.overlay.eyebrow")}</span>
            <span class="name" data-testid="formula-overlay-name">
              ${t(TEMPLATE_NAME_KEY[match.templateId])}
            </span>
          </div>
          <button
            class="close"
            type="button"
            data-testid="formula-overlay-close"
            @click=${toggleFormulaOverlay}
            aria-label=${t("formula.overlay.close")}
          >
            ✕
          </button>
        </div>
        <div class="body">
          <div class="field">
            <span class="field-label">${t("formula.overlay.symbolic")}</span>
            <div class="formula-host" data-host="symbolic"></div>
          </div>
          <div class="field">
            <span class="field-label">${t("formula.overlay.numeric")}</span>
            <div class="formula-host" data-host="numeric"></div>
          </div>
          <div class="result">
            <span class="result-value" data-testid="formula-overlay-result">
              ${this.renderResultValue(match)}
            </span>
            <span class="result-unit">${match.numeric.unit}</span>
          </div>
        </div>
        <div class="footer">
          ${this.renderDuration()}
          ${this._count.value > 1
            ? html`<span data-testid="formula-overlay-match-count"
                >${t("formula.overlay.match-count", { count: String(this._count.value) })}</span
              >`
            : nothing}
        </div>
      </div>
    `;
  }

  private renderResultValue(match: MatchResult): string {
    const v = match.numeric.result;
    if (!Number.isFinite(v)) return "—";
    const abs = Math.abs(v);
    if (abs === 0) return "0";
    if (abs >= 1 && abs < 1000) return v.toPrecision(4).replace(/\.?0+$/, "");
    if (abs < 1 && abs > 1e-6) {
      // mV / mA / ms range — show 3 sig figs.
      return v.toPrecision(3);
    }
    return v.toExponential(3);
  }

  private renderDuration() {
    const ms = this._duration.value;
    if (ms === null) return nothing;
    return html`<span data-testid="formula-overlay-duration"
      >${t("formula.overlay.duration", { ms: ms.toFixed(1) })}</span
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-formula-overlay": VxaFormulaOverlay;
  }
}
